// SAB-431: cookie consent state management + integration API.
//
// Public surface:
//   - hasConsent(category)                  → boolean, sync
//   - getConsent()                          → ConsentState | null
//   - needsBanner() / showConsentBanner()   → for the Banner.tsx mount
//   - acceptAll() / rejectAll() / setConsent() → write paths
//   - subscribeConsent() / useConsent()     → reactive (vanilla + React)
//   - getConsentHeader() / consentFetch()   → server-side bridge for SAB-437
//
// Persistence: localStorage AND a domain-scoped cookie. The cookie is what
// makes consent state visible to /blog (Vercel-rewritten to Kinsta) since
// it's set on .sabotagefantasy.com. localStorage is the fast read path on
// the SPA; cookie is the authoritative cross-subdomain fallback.
//
// Re-prompt: 13 months after the last decision (industry-standard expiry
// for consent records).
//
// Versioning: ConsentState.version starts at 1. If we add a new category
// (e.g. "personalization"), bump CURRENT_VERSION → previously-stored state
// at version < CURRENT_VERSION triggers needsBanner() = true so users get
// re-prompted for the new category.

import { Capacitor } from '@capacitor/core'

export type ConsentCategory = 'analytics' | 'marketing' | 'location'

export interface ConsentState {
  analytics: boolean
  marketing: boolean
  location: boolean
  /** Bump if categories change. v1 = {analytics, marketing, location}. */
  version: number
  /** ISO timestamp of the decision. */
  decided_at: string
  /** ISO timestamp; once `now > expires_at`, banner re-shows. */
  expires_at: string
  source: 'accept_all' | 'reject_all' | 'customize'
}

const CURRENT_VERSION = 1
const STORAGE_KEY = 'sf_consent_v1'
const COOKIE_NAME = 'sf_consent'
const EXPIRY_DAYS = 395 // ~13 months
const EXPIRY_MS = EXPIRY_DAYS * 24 * 60 * 60 * 1000

const CATEGORIES: readonly ConsentCategory[] = ['analytics', 'marketing', 'location'] as const

// SAB-706: on native (Capacitor) the web cookie-consent banner is suppressed —
// cookie consent is a web / ePrivacy mechanism, and Apple's frameworks govern on
// native (a cookie prompt in the WKWebView triggers App Store 5.1.2(i)). With no
// banner to collect a decision, native falls back to this default: analytics ON
// (first-party Mixpanel + GA4 product analytics), marketing/location OFF. Users
// retain control via the per-category toggles in Settings (CustomizeModal), which
// persist a real ConsentState that supersedes this default. Web is unchanged.
const IS_NATIVE = typeof Capacitor !== 'undefined' && Capacitor.isNativePlatform()
const NATIVE_DEFAULT: ConsentState = {
  analytics: true,
  marketing: false,
  location: false,
  version: CURRENT_VERSION,
  decided_at: '1970-01-01T00:00:00.000Z',
  expires_at: '9999-12-31T23:59:59.999Z',
  source: 'accept_all',
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Internal state                                                         */
/* ──────────────────────────────────────────────────────────────────────── */

let cached: ConsentState | null | undefined = undefined
let forceShowBanner = false
const listeners = new Set<(state: ConsentState | null) => void>()

function notify(): void {
  const state = getConsent()
  for (const cb of listeners) {
    try { cb(state) } catch { /* listener errors don't break the chain */ }
  }
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Persistence                                                            */
/* ──────────────────────────────────────────────────────────────────────── */

function readLocalStorage(): ConsentState | null {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as ConsentState
  } catch {
    return null
  }
}

function writeLocalStorage(state: ConsentState): void {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch { /* quota exceeded / private mode — fall through to cookie only */ }
}

function readCookie(): ConsentState | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + COOKIE_NAME.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'),
  )
  if (!match) return null
  try {
    return JSON.parse(decodeURIComponent(match[1])) as ConsentState
  } catch {
    return null
  }
}

function writeCookie(state: ConsentState): void {
  if (typeof document === 'undefined') return
  // Domain=.sabotagefantasy.com so /blog (Vercel rewrite to Kinsta) sees it.
  // On localhost / preview hosts the leading-dot domain attribute is ignored
  // by the browser and the cookie falls back to host-scoped — which is fine
  // because /blog only matters in prod.
  const value = encodeURIComponent(JSON.stringify(state))
  const maxAge = Math.floor(EXPIRY_MS / 1000)
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  const useDomain = host.endsWith('sabotagefantasy.com')
  const parts = [
    `${COOKIE_NAME}=${value}`,
    'Path=/',
    `Max-Age=${maxAge}`,
    'SameSite=Lax',
  ]
  if (useDomain) parts.push('Domain=.sabotagefantasy.com')
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') parts.push('Secure')
  document.cookie = parts.join('; ')
}

function deleteCookie(): void {
  if (typeof document === 'undefined') return
  const host = typeof window !== 'undefined' ? window.location.hostname : ''
  const useDomain = host.endsWith('sabotagefantasy.com')
  const parts = [`${COOKIE_NAME}=`, 'Path=/', 'Max-Age=0', 'SameSite=Lax']
  if (useDomain) parts.push('Domain=.sabotagefantasy.com')
  document.cookie = parts.join('; ')
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Internal validation                                                    */
/* ──────────────────────────────────────────────────────────────────────── */

function isValid(state: ConsentState | null): state is ConsentState {
  if (!state) return false
  if (state.version !== CURRENT_VERSION) return false
  if (typeof state.expires_at !== 'string') return false
  if (new Date(state.expires_at).getTime() < Date.now()) return false
  for (const cat of CATEGORIES) {
    if (typeof state[cat] !== 'boolean') return false
  }
  return true
}

function loadFromStorage(): ConsentState | null {
  // Prefer localStorage (fast); fall back to cookie; if cookie exists but
  // localStorage is missing, mirror cookie → localStorage so subsequent
  // reads avoid the cookie parse.
  const ls = readLocalStorage()
  if (isValid(ls)) return ls
  const cookie = readCookie()
  if (isValid(cookie)) {
    writeLocalStorage(cookie)
    return cookie
  }
  return null
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Public — READ                                                          */
/* ──────────────────────────────────────────────────────────────────────── */

export function getConsent(): ConsentState | null {
  if (cached === undefined) cached = loadFromStorage()
  // SAB-706: native has no consent banner; absent a stored decision, return the
  // native default (analytics on) so product analytics initialize and
  // needsBanner() stays false. `cached` remains null, so a later setConsent() from
  // the Settings toggles persists a real state that supersedes this.
  if (cached === null && IS_NATIVE) return NATIVE_DEFAULT
  return cached
}

export function hasConsent(category: ConsentCategory): boolean {
  const state = getConsent()
  if (!state) return false
  return state[category] === true
}

export function needsBanner(): boolean {
  return getConsent() === null || forceShowBanner
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Public — WRITE                                                         */
/* ──────────────────────────────────────────────────────────────────────── */

function persist(state: ConsentState): void {
  cached = state
  forceShowBanner = false
  writeLocalStorage(state)
  writeCookie(state)
  notify()
}

function build(
  flags: Record<ConsentCategory, boolean>,
  source: ConsentState['source'],
): ConsentState {
  const now = new Date()
  return {
    analytics: flags.analytics,
    marketing: flags.marketing,
    location: flags.location,
    version: CURRENT_VERSION,
    decided_at: now.toISOString(),
    expires_at: new Date(now.getTime() + EXPIRY_MS).toISOString(),
    source,
  }
}

export function acceptAll(): void {
  persist(build({ analytics: true, marketing: true, location: true }, 'accept_all'))
}

export function rejectAll(): void {
  persist(build({ analytics: false, marketing: false, location: false }, 'reject_all'))
}

export function setConsent(flags: Record<ConsentCategory, boolean>): void {
  persist(build(flags, 'customize'))
}

/** Clear stored consent and force the banner to re-appear. Used by the
 *  "Manage cookie preferences" link in the footer. */
export function resetConsent(): void {
  cached = null
  forceShowBanner = true
  if (typeof window !== 'undefined' && window.localStorage) {
    try { window.localStorage.removeItem(STORAGE_KEY) } catch { /* */ }
  }
  deleteCookie()
  notify()
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Public — REACTIVE                                                      */
/* ──────────────────────────────────────────────────────────────────────── */

export function subscribeConsent(cb: (state: ConsentState | null) => void): () => void {
  listeners.add(cb)
  return () => { listeners.delete(cb) }
}

/* ──────────────────────────────────────────────────────────────────────── */
/*  Public — SERVER-SIDE BRIDGE                                            */
/* ──────────────────────────────────────────────────────────────────────── */

/** Emit a header value that edge functions can parse via
 *  supabase/functions/_shared/consent.ts. Format: `analytics=1,marketing=0,location=1`.
 *  Returns '' if no decision yet (server-side treats absent header as deny-all). */
export function getConsentHeader(): string {
  const s = getConsent()
  if (!s) return ''
  return [
    `analytics=${s.analytics ? 1 : 0}`,
    `marketing=${s.marketing ? 1 : 0}`,
    `location=${s.location ? 1 : 0}`,
  ].join(',')
}

/** Drop-in fetch replacement that auto-injects the x-sf-consent header.
 *  Use this from any client code calling an edge function that gates on
 *  consent (e.g. SAB-437's location-based eligibility checks). */
export async function consentFetch(input: RequestInfo, init?: RequestInit): Promise<Response> {
  const header = getConsentHeader()
  const headers = new Headers(init?.headers ?? {})
  if (header) headers.set('x-sf-consent', header)
  return fetch(input, { ...init, headers })
}
